<template>
  <CForm>
    <section class="app-section">
      <h3 class="app-section-head">Identity</h3>
      <div class="app-section-body">
        <CRow class="g-3">
          <CCol xs="12" sm="7">
            <CFormInput id="tenant-name" v-model="detail.name" type="text" label="Name"
              placeholder="My tenant"
              text="Between 4 and 32 characters. A name nobody else uses saves confusion later."
              :disabled="loading" />
          </CCol>
          <CCol xs="12" sm="5">
            <CFormInput id="tenant-zabbix" v-model="detail.zabbix" type="text" label="Short name"
              placeholder="ACME" text="Exactly four characters. Set once, when the proxy is installed."
              maxlength="4"
              :disabled="loading || !creating" :invalid="creating && !validCode()" />
          </CCol>
        </CRow>
      </div>
    </section>

    <section class="app-section">
      <h3 class="app-section-head">Agent</h3>
      <div class="app-section-body">
        <CFormSwitch id="tenant-monitor" v-model="detail.monitor" :disabled="loading"
          label="A zAgent is installed for this customer" />
        <p class="text-body-secondary small mt-2 mb-0">
          With this off the customer is not monitored at all. With it on, the dot beside the
          customer says what the agent is doing.
        </p>

        <dl class="app-legend">
          <div>
            <dt><span class="app-dot bg-success"></span></dt>
            <dd>the proxy is installed and the agent is reporting</dd>
          </div>
          <div>
            <dt><span class="app-dot bg-danger"></span></dt>
            <dd>the agent has stopped reporting, or has failed</dd>
          </div>
          <div>
            <dt><span class="app-dot bg-secondary"></span></dt>
            <dd>no agent is installed, so there is nothing to report</dd>
          </div>
        </dl>
      </div>
    </section>

    <section class="app-section">
      <h3 class="app-section-head">IT Glue</h3>
      <div class="app-section-body">
        <CRow class="g-3">
          <CCol xs="12" sm="4">
            <CFormInput v-model.number="detail.glue.id" type="number" min="0" label="Identifier"
              placeholder="2170428756377853" :disabled="loading" />
          </CCol>
          <CCol xs="12" sm="8">
            <CFormInput v-model="detail.glue.url" type="text" label="Address"
              placeholder="https://tenant.eu.itglue.com/" :disabled="loading" />
          </CCol>
        </CRow>
      </div>
    </section>
  </CForm>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { create, update, get } from '@/libraries/tenant'
import { success } from '@/libraries/toast'
import { factory as factoryTenant } from '@/components/tenant/interfaces'
import { editableGlue, withGlue } from '@/libraries/utilities'
import type { WriteResult } from '@/types/api'

export default defineComponent({
  name: 'TenantForm',
  props: {
    tenant: {
      type: String,
      default: '',
    },
  },
  emits: ['saved'],
  data() {
    return {
      loading: false,
      detail: factoryTenant(),
    }
  },
  computed: {
    creating(): boolean {
      return typeof this.detail._key === 'undefined'
    },
  },
  watch: {
    tenant: {
      handler: 'load',
      immediate: true,
    },
  },
  methods: {
    load(key: string): void {
      if (!key) return
      this.loading = true
      get(key).then((response) => {
        this.loading = false
        if (!response) return
        this.detail = { ...response, glue: editableGlue(response.glue) }
      })
    },
    save(): void {
      if (this.loading || this.deactivate()) return
      this.loading = true
      const key = this.detail._key
      const body = withGlue(this.detail)
      const write = key === undefined ? create(body) : update(key, body)
      write.then(this.finish)
    },
    /* The API stores exactly four characters, and refuses anything else. */
    validCode(): boolean {
      return (this.detail.zabbix ?? '').trim().length === 4
    },
    finish(response: WriteResult | null): void {
      this.loading = false
      if (!response || typeof response !== 'object') return
      success()
      this.$emit('saved')
    },
    deactivate(): boolean {
      if (typeof this.detail.name !== 'string') return true
      if (this.detail.name.length < 4 || this.detail.name.length > 32) return true
      /* Only on creation: the short name cannot be changed afterwards, so an
         existing tenant stays editable whatever it holds. */
      return this.creating && !this.validCode()
    },
  },
})
</script>
